(() => {
  'use strict';
  if (window.teWinterGifts) return;
  const initial = document.querySelector('[data-te-gift-config]');
  if (!initial) return;
  let config;
  try { config = JSON.parse(initial.textContent); } catch { return; }
  const root = window.Shopify?.routes?.root || '/';
  const marker = '_te_gift';
  const campaign = 'winter-2026:';
  const escape = value => String(value ?? '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  const normalize = text => String(text).replace(/[’']/g, "'").toLowerCase();
  const views = new Set();
  const choices = {};
  let preview = {};
  let cart = null;
  let syncTask = null;
  const approvedForms = new WeakSet();
  let storedToken = null;
  let pending = false;
  let writing = false;
  let notice = '';
  let counter = 0;
  const active = () => config.state === 'active' && Date.now() <= config.end;
  const demo = () => config.state === 'preview';
  const text = key => config.text[key] || key;
  const owned = item => String(item.properties?.[marker] || '').startsWith(campaign);
  const isRuleGift = (item, rule) => rule.gifts.some(p => p.id === item.product_id);
  const allocation = (item, rule) => (item.line_level_discount_allocations || [])
    .filter(a => normalize(a.discount_application?.title) === normalize(rule.discountTitle))
    .reduce((n, a) => n + a.amount, 0);
  function analyse(currentCart, rule) {
    const due = currentCart.items.filter(i => !owned(i) && (rule.triggers || []).includes(i.product_id))
      .reduce((n, i) => n + i.quantity * rule.perTrigger, 0);
    const lines = currentCart.items.filter(i => isRuleGift(i, rule));
    const present = lines.reduce((n, i) => n + i.quantity, 0);
    const free = lines.reduce((n, i) => {
      const unit = i.original_price || i.price;
      return n + (unit > 0 ? Math.min(i.quantity, Math.floor(allocation(i, rule) / unit)) : 0);
    }, 0);
    return { rule, due, lines, present, free, missing: Math.max(0, due - present) };
  }
  window.teWinterGifts = { analyse };
  async function api(path, body) {
    const response = await fetch(root + path, {
      method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
      headers: {'Accept':'application/json', ...(body ? {'Content-Type':'application/json'} : {})},
      ...(body ? {body:JSON.stringify(body)} : {})
    });
    if (!response.ok) throw new Error('cart-request');
    return response.json();
  }
  async function fresh() {
    const response = await fetch(root + '?section_id=te-gift-data', {credentials:'same-origin', cache:'no-store'});
    if (!response.ok) throw new Error('gift-config');
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const data = doc.querySelector('[data-te-gift-config]');
    if (!data) throw new Error('gift-config');
    config = JSON.parse(data.textContent);
    cart = await api('cart.js');
    if (storedToken !== cart.token) {
      storedToken = cart.token;
      try { Object.assign(choices, JSON.parse(sessionStorage.getItem('teWinterGiftChoices:' + cart.token) || '{}')); } catch {}
    }
  }
  function savePreview() {
    try { sessionStorage.setItem('teWinterGifts:' + cart.token, JSON.stringify(preview)); } catch {}
  }
  function saveChoices() {
    if (!cart) return;
    try { sessionStorage.setItem('teWinterGiftChoices:' + cart.token, JSON.stringify(choices)); } catch {}
  }
  function trimChoices() {
    for (const rule of config.rules) {
      const state = analyse(cart, rule);
      const c = selection(rule);
      c.products.length = Math.min(c.products.length, state.missing);
      c.sizes.length = Math.min(c.sizes.length, state.missing);
      if (!state.due) { c.skip = false; delete preview[rule.id]; }
    }
    saveChoices();
  }
  async function removeOwnedExtras() {
    let changed = false;
    for (const rule of config.rules) {
      const state = analyse(cart, rule);
      const ownLines = state.lines.filter(owned);
      const ordinary = state.lines.filter(i => !owned(i)).reduce((n, i) => n + i.quantity, 0);
      let allowance = active() ? Math.max(0, state.due - ordinary) : 0;
      for (const old of ownLines) {
        const line = cart.items.find(i => i.variant_id === old.variant_id && i.properties?.[marker] === old.properties?.[marker]);
        if (!line) continue;
        const keep = Math.min(allowance, line.quantity);
        allowance -= keep;
        if (keep < line.quantity) {
          await api('cart/change.js', {id:line.key, quantity:keep});
          cart = await api('cart.js');
          changed = true;
        }
      }
    }
    return changed;
  }
  function refreshTheme() {
    document.dispatchEvent(new CustomEvent('theme:cart:refresh', {bubbles:true}));
    if (/\/cart\/?$/.test(location.pathname)) {
      const manager = document.querySelector('cart-items');
      if (typeof manager?.getCart === 'function') manager.getCart();
    }
  }
  function sync() {
    if (writing) { pending = true; return Promise.resolve(); }
    if (syncTask) return syncTask;
    syncTask = (async () => {
      try {
        await fresh();
        trimChoices();
        if (!demo() && await removeOwnedExtras()) refreshTheme();
        document.querySelectorAll('[data-te-gift-offer]').forEach(el => { el.hidden = !active() && !demo(); });
        for (const view of views) if (view.isConnected) view.render();
      } catch {
        for (const view of views) if (view.isConnected) view.error(text('unavailable'));
      } finally { syncTask = null; }
    })();
    return syncTask;
  }
  function options(rule) {
    return rule.gifts.filter(p => p.variants.some(v => v.available && v.capacity > 0));
  }
  function selection(rule) {
    return choices[rule.id] ||= {products:[], sizes:[], skip:false};
  }
  function chosenVariants(rule, count) {
    const c = selection(rule);
    return Array.from({length:count}, (_, i) => {
      const p = rule.gifts.find(p => p.id === Number(c.products[i]));
      if (!p) return null;
      const size = c.sizes[i];
      return rule.id === 'sans-manches'
        ? p.variants.find(v => v.title === size && v.available)
        : p.variants.find(v => v.available);
    });
  }
  function sizeSelect(view, rule, index, value, variants) {
    const id = `te-gift-size-${view.uid}-${rule.id}-${index}`;
    return `<label for="${id}">${escape(text('size'))} · ${escape(text('pair'))} ${index + 1}</label>
      <select id="${id}" data-size="${index}" aria-required="true">
        <option value="">${escape(text('choose_size'))}</option>
        ${variants.map(v => `<option value="${escape(v)}"${v === value ? ' selected' : ''}>${escape(v)}</option>`).join('')}
      </select>`;
  }
  function editor(view, state) {
    const rule = state.rule;
    const c = selection(rule);
    const products = options(rule);
    if (!products.length) return `<p>${escape(text('sold_out'))}</p>`;
    let html = '';
    for (let i = 0; i < state.missing; i++) {
      html += `<fieldset><legend>${escape(rule.id === 'sans-manches' ? text('pair') : text('colour'))}${state.missing > 1 ? ' ' + (i + 1) : ''}</legend><div class="te-gift-choices">
        ${products.map(p => `<label class="te-gift-choice">
          <input type="radio" name="te-gift-${view.uid}-${rule.id}-${i}" data-product="${i}" value="${p.id}"${Number(c.products[i]) === p.id ? ' checked' : ''}>
          ${p.image ? `<img src="${escape(p.image)}" width="120" height="120" loading="lazy" alt="">` : ''}
          <span>${escape(p.title)}</span></label>`).join('')}
        </div>`;
      if (rule.id === 'sans-manches') {
        const selected = products.find(p => p.id === Number(c.products[i]));
        const sizes = [...new Set((selected ? [selected] : products).flatMap(p => p.variants.filter(v => v.available).map(v => v.title)))];
        html += sizeSelect(view, rule, i, c.sizes[i], sizes);
      }
      html += '</fieldset>';
    }
    return html;
  }
  function renderViews() {
    for (const view of views) if (view.isConnected) view.render();
  }
  class GiftPicker extends HTMLElement {
    connectedCallback() {
      this.uid ||= ++counter;
      views.add(this);
      this.addEventListener('change', this.change);
      if (cart) this.render();
      else sync();
    }
    disconnectedCallback() {
      views.delete(this);
      this.removeEventListener('change', this.change);
    }
    error(message) {
      const status = this.querySelector('[role=status]');
      if (status) status.textContent = message;
    }
    render() {
      if (!cart) return;
      const states = config.rules.map(rule => analyse(cart, rule)).filter(s => s.due > 0);
      this.hidden = !states.length || (!demo() && !active());
      if (this.hidden) return;
      this.innerHTML = `<h3>${escape(text('heading'))}</h3>
        ${demo() ? `<small class="te-gift-preview">${escape(text('preview'))}</small>` : ''}
        ${states.map(s => `<section class="te-gift-rule" data-rule="${s.rule.id}" aria-labelledby="te-gift-title-${this.uid}-${s.rule.id}">
          <h4 id="te-gift-title-${this.uid}-${s.rule.id}">${escape(s.rule.title)}</h4>
          ${!selection(s.rule).skip ? (s.missing ? editor(this, s) : `<p>${escape(s.free >= s.due ? text('confirmed') : text('check_discount'))}</p>`) : ''}
          <label class="te-gift-skip"><input type="checkbox" data-skip${selection(s.rule).skip ? ' checked' : ''}>${escape(text('skip'))}</label>
        </section>`).join('')}
        <p class="te-gift-status" role="status" aria-live="polite" tabindex="-1">${escape(notice || text('checkout_hint'))}</p>`;
      this.querySelectorAll('input, select').forEach(el => { el.disabled = writing; });
    }
    change = e => {
      const block = e.target.closest('[data-rule]');
      if (!block || writing) return;
      const c = selection(config.rules.find(r => r.id === block.dataset.rule));
      if (e.target.hasAttribute('data-product')) c.products[Number(e.target.dataset.product)] = Number(e.target.value);
      if (e.target.hasAttribute('data-size')) c.sizes[Number(e.target.dataset.size)] = e.target.value;
      if (e.target.hasAttribute('data-skip')) c.skip = e.target.checked;
      notice = '';
      saveChoices();
      const selector = e.target.hasAttribute('data-product')
        ? `[data-rule="${block.dataset.rule}"] [data-product="${e.target.dataset.product}"][value="${e.target.value}"]`
        : e.target.hasAttribute('data-size') ? `[data-rule="${block.dataset.rule}"] [data-size="${e.target.dataset.size}"]`
        : `[data-rule="${block.dataset.rule}"] [data-skip]`;
      renderViews();
      this.querySelector(selector)?.focus({preventScroll:true});
    };
  }
  function fail(code, rule, index, field) {
    const error = new Error(code);
    Object.assign(error, {rule:rule?.id, index, field});
    throw error;
  }
  function focusFeedback(error) {
    const view = [...views].find(v => v.isConnected && !v.hidden && v.getBoundingClientRect().height > 0);
    if (!view) return;
    const target = (error.rule && error.field
      ? view.querySelector(`[data-rule="${error.rule}"] [data-${error.field}="${error.index}"]`)
      : null) || view.querySelector('[role=status]');
    if (target) {
      target.scrollIntoView({block:'center', behavior:'instant'});
      if (error.field) target.setAttribute('aria-invalid','true');
      target.focus({preventScroll:true});
    }
  }
  // Intercept only the native cart checkout submission. Resume it after validation,
  // preserving the theme's terms checks, note fields and Shopify checkout routing.
  document.addEventListener('submit', async event => {
    const form = event.target;
    const button = event.submitter;
    if (!form.matches('[data-cart-form]') || button?.name !== 'checkout') return;
    if (approvedForms.has(form)) { approvedForms.delete(form); return; }
    if (!demo() && !active() && !writing) return;
    const terms = form.querySelector('[data-cart-acceptance-checkbox]');
    if (terms && !terms.checked) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (writing) return;
    writing = true;
    button.disabled = true;
    button.setAttribute('aria-busy','true');
    let batch = '';
    let resume = false;
    let failure = null;
    try {
      if (syncTask) await syncTask;
      renderViews();
      await fresh();
      if (!demo() && !active()) fail('expired');
      if (!demo()) await removeOwnedExtras();
      const planned = [];
      for (const rule of config.rules) {
        const state = analyse(cart, rule);
        const c = selection(rule);
        if (!state.due || c.skip) continue;
        if (!demo() && state.free < Math.min(state.due, state.present)) fail('discount',rule);
        const variants = chosenVariants(rule, state.missing);
        variants.forEach((v, index) => {
          if (!v) fail('selection', rule, index, c.products[index] ? 'size' : 'product');
        });
        const quantities = {};
        for (const v of variants) quantities[v.id] = (quantities[v.id] || 0) + 1;
        for (const v of variants) {
          const existing = cart.items.filter(i => i.variant_id === v.id).reduce((n,i) => n+i.quantity,0);
          if (quantities[v.id] + existing > v.capacity) fail('stock',rule);
        }
        planned.push({rule, variants, quantities});
      }
      if (!config.rules.some(rule => analyse(cart, rule).due > 0)) {
        resume = true;
      } else if (demo()) {
        preview = Object.fromEntries(planned.map(p => [p.rule.id, p.variants.map(v => v.id)]));
        savePreview();
        notice = text('checkout_preview');
      } else {
        const items = planned.flatMap(p => Object.entries(p.quantities).map(([id,quantity]) => ({id:Number(id),quantity})));
        if (items.length) {
          batch = campaign + 'checkout:' + Date.now();
          await api('cart/add.js',{items:items.map(i => ({...i,properties:{[marker]:batch}}))});
          cart = await api('cart.js');
        }
        for (const plan of planned) {
          const state = analyse(cart,plan.rule);
          if (state.free < state.due) fail('discount',plan.rule);
        }
        resume = true;
      }
    } catch (error) {
      failure = error;
      let cleanupFailed = false;
      if (batch) {
        try {
          cart = await api('cart.js');
          while (cart.items.some(i => i.properties?.[marker] === batch)) {
            const line = cart.items.find(i => i.properties?.[marker] === batch);
            await api('cart/change.js',{id:line.key,quantity:0});
            cart = await api('cart.js');
          }
          refreshTheme();
        } catch { cleanupFailed = true; }
      }
      notice = text(cleanupFailed ? 'cleanup_error' : ({selection:'choose_required',stock:'stock_changed',expired:'expired',discount:'discount_error'}[error.message] || 'unavailable'));
    } finally {
      writing = false;
      button.disabled = false;
      button.removeAttribute('aria-busy');
      renderViews();
      if (resume) {
        approvedForms.add(form);
        form.requestSubmit(button);
      } else {
        focusFeedback(failure || {});
      }
      if (pending) { pending = false; sync(); }
    }
  }, true);
  customElements.define('te-gift-picker', GiftPicker);
  for (const name of ['theme:cart:added','theme:cart:loaded','theme:cart:open','shopify:section:load']) {
    document.addEventListener(name, () => { if (!writing) sync(); else pending = true; });
  }
  document.addEventListener('visibilitychange', () => { if (!document.hidden) sync(); });
  window.addEventListener('pageshow', sync);
  setInterval(() => { if (!document.hidden && !demo() && config.state === 'active' && Date.now() > config.end) sync(); }, 30000);
})();
